import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });

  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [lockInfo, setLockInfo] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);

  // Load error state from localStorage on component mount
  useEffect(() => {
    console.log('🔍 Checking for saved error state...');
    const savedError = localStorage.getItem('loginError');
    const savedLockInfo = localStorage.getItem('loginLockInfo');
    
    console.log('Saved error:', savedError);
    console.log('Saved lock info:', savedLockInfo);
    
    if (savedError && savedLockInfo) {
      try {
        const parsedLockInfo = JSON.parse(savedLockInfo);
        if (parsedLockInfo.lockedUntil) {
          const lockTime = new Date(parsedLockInfo.lockedUntil);
          const now = new Date();
          
          console.log('Lock time:', lockTime);
          console.log('Current time:', now);
          console.log('Is lock still active?', lockTime > now);
          
          // Only restore if lockout is still active
          if (lockTime > now) {
            console.log('🔄 Restoring lockout state from localStorage');
            setError(savedError);
            setLockInfo(parsedLockInfo);
          } else {
            console.log('⏰ Lockout expired, clearing localStorage');
            // Clear expired lockout
            localStorage.removeItem('loginError');
            localStorage.removeItem('loginLockInfo');
          }
        }
      } catch (e) {
        console.error('Error parsing saved lock info:', e);
        localStorage.removeItem('loginError');
        localStorage.removeItem('loginLockInfo');
      }
    }
  }, []);

  // Countdown timer effect for account lockout
  useEffect(() => {
    let interval = null;
    
    if (lockInfo?.lockedUntil) {
      const updateCountdown = () => {
        const now = new Date();
        const lockTime = new Date(lockInfo.lockedUntil);
        const diff = lockTime - now;
        
        if (diff > 0) {
          const minutes = Math.floor(diff / (1000 * 60));
          const seconds = Math.floor((diff % (1000 * 60)) / 1000);
          setTimeLeft({ minutes, seconds, total: diff });
        } else {
          setTimeLeft({ minutes: 0, seconds: 0, total: 0 });
          setLockInfo(null);
          setError(null);
          // Clear localStorage when lockout expires
          localStorage.removeItem('loginError');
          localStorage.removeItem('loginLockInfo');
          clearInterval(interval);
        }
      };
      
      updateCountdown(); // Initial call
      interval = setInterval(updateCountdown, 1000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [lockInfo?.lockedUntil]);

  const formatTimeLeft = (timeLeft) => {
    if (!timeLeft) return '';
    
    const { minutes, seconds } = timeLeft;
    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setLockInfo(null);
    
    try {
      const result = await login(formData);
      console.log('Login result:', result); // Debug log
      
      if (result.success) {
        // Redirect based on user role
        const userRole = result.user?.role;
        let redirectPath = '/';
        
        if (userRole === 'STUDENT') {
          redirectPath = '/student';
        } else if (['SUPER_ADMIN', 'ADMIN', 'MODERATOR'].includes(userRole)) {
          redirectPath = '/admin';
        }
        
        console.log('Redirecting to:', redirectPath); // Debug log
        navigate(redirectPath, { replace: true });
      } else {
        setError(result.message || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      console.log('Error response status:', error.response?.status);
      console.log('Error response data:', error.response?.data);
      
      // Handle specific error cases
      if (error.response?.status === 423) {
        console.log('🔒 423 Error detected! Setting lockout state...');
        const errorData = error.response.data;
        const errorMessage = '🔒 Account Locked';
        setError(errorMessage);
        
        if (errorData.error?.lockedUntil) {
          const lockTime = new Date(errorData.error.lockedUntil);
          const lockInfoData = {
            lockedUntil: lockTime,
            message: `Account locked due to multiple failed attempts. Please wait.`,
            isLocked: true
          };
          
          console.log('Setting lock info:', lockInfoData);
          setLockInfo(lockInfoData);
          
          // Save to localStorage to persist across page reloads
          localStorage.setItem('loginError', errorMessage);
          localStorage.setItem('loginLockInfo', JSON.stringify(lockInfoData));
          console.log('Saved to localStorage for persistence');
        }
      } else if (error.response?.status === 401) {
        setError('❌ Invalid Login');
        setLockInfo({
          message: 'Please recheck your email and password.',
          isCredentialsError: true
        });
      } else if (error.response?.status === 429) {
        setError('⏰ Too Many Attempts');
        setLockInfo({
          message: 'Too many attempts. Please wait a few minutes.',
          isRateLimitError: true
        });
      } else if (error.response?.status === 500) {
        setError('🔧 Server Error');
        setLockInfo({
          message: 'Server error. Please try again.',
          isServerError: true
        });
      } else if (error.response?.status === 0 || !error.response) {
        setError('🌐 Connection Error');
        setLockInfo({
          message: 'Check your internet connection.',
          isConnectionError: true
        });
      } else {
        setError('❌ Login Failed');
        setLockInfo({
          message: error.response?.data?.message || 'Something went wrong. Please try again.',
          isGenericError: true
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing (but not for lockout errors)
    if (error && !lockInfo?.isLocked) {
      setError(null);
      setLockInfo(null);
      localStorage.removeItem('loginError');
      localStorage.removeItem('loginLockInfo');
    }
  };

  return (
    <div className="login-page" style={{
      minHeight: '100dvh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 'max(16px, env(safe-area-inset-top)) max(16px, env(safe-area-inset-right)) max(16px, env(safe-area-inset-bottom)) max(16px, env(safe-area-inset-left))',
      position: 'relative',
      overflow: 'hidden',
      WebkitOverflowScrolling: 'touch'
    }}>
      {/* Animated Background Elements */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: 'hidden',
        zIndex: 1
      }}>
        <div style={{
          position: 'absolute',
          top: '10%',
          left: '10%',
          width: '100px',
          height: '100px',
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '50%',
          animation: 'float 6s ease-in-out infinite'
        }}></div>
        <div style={{
          position: 'absolute',
          top: '60%',
          right: '15%',
          width: '150px',
          height: '150px',
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '50%',
          animation: 'float 8s ease-in-out infinite reverse'
        }}></div>
        <div style={{
          position: 'absolute',
          bottom: '20%',
          left: '20%',
          width: '80px',
          height: '80px',
          background: 'rgba(255, 255, 255, 0.08)',
          borderRadius: '50%',
          animation: 'float 7s ease-in-out infinite'
        }}></div>
      </div>

      {/* Login Card */}
      <div className="login-card" style={{
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderRadius: '20px',
        padding: '36px',
        width: '100%',
        maxWidth: '480px',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        position: 'relative',
        zIndex: 2
      }}>
        {/* Header */}
        <div className="header" style={{ textAlign: 'center', marginBottom: '24px' }}>
          <div className="header-icon" style={{
            width: '56px',
            height: '56px',
            background: 'linear-gradient(135deg, #667eea, #764ba2)',
            borderRadius: '14px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
            boxShadow: '0 10px 25px rgba(102, 126, 234, 0.3)'
          }}>
            <svg 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24" 
              style={{ width: '28px', height: '28px', color: 'white' }}
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h1 style={{
            fontSize: '24px',
            fontWeight: '700',
            color: '#1f2937',
            margin: '0 0 6px 0'
          }}>
            Welcome Back
          </h1>
          <p style={{
            fontSize: '14px',
            color: '#6b7280',
            margin: 0
          }}>
            Sign in to your Exam profile 
          </p>
        </div>

        {/* Error Display */}
        {error && (
          <div className={`error-message ${lockInfo ? 'lock-error' : ''}`} style={{
            background: lockInfo ? 'linear-gradient(135deg, #fef3c7, #fde68a)' : 'linear-gradient(135deg, #fee2e2, #fecaca)',
            border: lockInfo ? '1px solid #f59e0b' : '1px solid #ef4444',
            borderRadius: '12px',
            padding: '12px',
            marginBottom: '20px',
            color: lockInfo ? '#92400e' : '#dc2626',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '10px'
          }}>
            <div style={{
              flexShrink: 0,
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginTop: '2px',
              fontSize: '20px'
            }}>
              {lockInfo?.isCredentialsError ? '🔑' :
               lockInfo?.isRateLimitError ? '⏰' :
               lockInfo?.isServerError ? '🔧' :
               lockInfo?.isConnectionError ? '🌐' :
               lockInfo?.isGenericError ? '⚠️' :
               lockInfo ? '🔒' : '❌'}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{
                fontWeight: '700',
                fontSize: '15px',
                marginBottom: '6px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                {error}
              </div>
              {lockInfo && (
                <div style={{
                  fontSize: '13px',
                  opacity: 0.9,
                  lineHeight: '1.4',
                  marginBottom: '8px'
                }}>
                  {lockInfo.message}
                </div>
              )}
              {lockInfo?.lockedUntil && (
                <div className="lock-info" style={{
                  background: 'rgba(0, 0, 0, 0.05)',
                  borderRadius: '8px',
                  padding: '12px',
                  fontSize: '13px',
                  lineHeight: '1.4'
                }}>
                  <div style={{ fontWeight: '600', marginBottom: '8px', color: 'inherit', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <span>⏱️ Unlocks in:</span>
                    {timeLeft && (
                      <span style={{
                        background: 'rgba(0, 0, 0, 0.1)',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontFamily: 'monospace',
                        fontSize: '12px',
                        fontWeight: '700',
                        color: '#92400e',
                        whiteSpace: 'nowrap'
                      }}>
                        {formatTimeLeft(timeLeft)}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '11px', opacity: 0.6, fontStyle: 'italic' }}>
                    Wait for countdown to complete.
                  </div>
                </div>
              )}
              {lockInfo?.isConnectionError && (
                <div className="troubleshooting-tips" style={{
                  background: 'rgba(0, 0, 0, 0.05)',
                  borderRadius: '8px',
                  padding: '12px',
                  fontSize: '13px',
                  lineHeight: '1.4'
                }}>
                  <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                    💡 Troubleshooting tips:
                  </div>
                  <ul style={{ margin: '4px 0', paddingLeft: '16px', fontSize: '12px', opacity: 0.8 }}>
                    <li>Check your internet connection</li>
                    <li>Try refreshing the page</li>
                    <li>Contact support if the problem persists</li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Login Form */}
        <form onSubmit={handleSubmit} style={{ marginBottom: '20px' }}>
          {/* Email Field */}
          <div style={{ marginBottom: '20px' }}>
            <label htmlFor="email" style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '600',
              color: '#374151',
              marginBottom: '8px'
            }}>
              Email Address
            </label>
            <div style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center'
            }}>
              <div style={{
                position: 'absolute',
                left: '16px',
                display: 'flex',
                alignItems: 'center',
                color: '#9ca3af'
              }}>
                <svg 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24" 
                  style={{ width: '20px', height: '20px' }}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                </svg>
              </div>
              <input
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter your email address"
                style={{
                  width: '100%',
                  padding: '16px 16px 16px 48px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '12px',
                  fontSize: '16px',
                  transition: 'all 0.3s ease',
                  backgroundColor: 'white',
                  outline: 'none'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#667eea';
                  e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>
          </div>

          {/* Password Field */}
          <div style={{ marginBottom: '20px' }}>
            <label htmlFor="password" style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '600',
              color: '#374151',
              marginBottom: '8px'
            }}>
              Password
            </label>
            <div style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center'
            }}>
              <div style={{
                position: 'absolute',
                left: '16px',
                display: 'flex',
                alignItems: 'center',
                color: '#9ca3af'
              }}>
                <svg 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24" 
                  style={{ width: '20px', height: '20px' }}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                required
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter your password"
                style={{
                  width: '100%',
                  padding: '16px 16px 16px 48px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '12px',
                  fontSize: '16px',
                  transition: 'all 0.3s ease',
                  backgroundColor: 'white',
                  outline: 'none'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#667eea';
                  e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.boxShadow = 'none';
                }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '16px',
                  background: 'none',
                  border: 'none',
                  color: '#9ca3af',
                  cursor: 'pointer',
                  padding: '4px',
                  borderRadius: '4px',
                  transition: 'color 0.3s ease'
                }}
                onMouseEnter={(e) => e.target.style.color = '#667eea'}
                onMouseLeave={(e) => e.target.style.color = '#9ca3af'}
              >
                {showPassword ? (
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ width: '20px', height: '20px' }}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                  </svg>
                ) : (
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ width: '20px', height: '20px' }}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Remember Me */}
          <div className="remember-me" style={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            marginBottom: '20px'
          }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              fontSize: '14px',
              color: '#374151'
            }}>
              <input
                type="checkbox"
                checked={formData.rememberMe}
                onChange={(e) => setFormData(prev => ({ ...prev, rememberMe: e.target.checked }))}
                style={{
                  marginRight: '8px',
                  width: '16px',
                  height: '16px',
                  accentColor: '#667eea'
                }}
              />
              Remember me
            </label>
          </div>

          {/* Login Button */}
          <button
            type="submit"
            disabled={isLoading || lockInfo?.isLocked}
            style={{
              width: '100%',
              padding: '16px',
              background: lockInfo?.isLocked 
                ? 'linear-gradient(135deg, #9ca3af, #6b7280)' 
                : 'linear-gradient(135deg, #667eea, #764ba2)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: (isLoading || lockInfo?.isLocked) ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease',
              opacity: (isLoading || lockInfo?.isLocked) ? 0.7 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              boxShadow: lockInfo?.isLocked 
                ? '0 4px 14px rgba(156, 163, 175, 0.3)' 
                : '0 4px 14px rgba(102, 126, 234, 0.3)'
            }}
            onMouseEnter={(e) => {
              if (!isLoading && !lockInfo?.isLocked) {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.4)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isLoading && !lockInfo?.isLocked) {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 14px rgba(102, 126, 234, 0.3)';
              }
            }}
          >
            {isLoading ? (
              <>
                <div style={{
                  width: '20px',
                  height: '20px',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  borderTop: '2px solid white',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
                <span>Signing in...</span>
              </>
            ) : lockInfo?.isLocked ? (
              <>
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ width: '20px', height: '20px' }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <span>Account Locked</span>
              </>
            ) : (
              'Sign in to Dashboard'
            )}
          </button>
        </form>



        {/* Footer */}
        <div style={{
          textAlign: 'center',
          marginTop: '24px',
          paddingTop: '20px',
          borderTop: '1px solid #e5e7eb'
        }}>
          <p style={{
            fontSize: '12px',
            color: '#9ca3af',
            margin: '0 0 4px 0'
          }}>
            © 2025 Aria Delta Consulting Group. All rights reserved.
          </p>
          <a 
            href="https://www.ariadelta.af" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{
              fontSize: '11px',
              color: '#667eea',
              textDecoration: 'none',
              transition: 'color 0.3s ease'
            }}
            onMouseEnter={(e) => e.target.style.color = '#5a67d8'}
            onMouseLeave={(e) => e.target.style.color = '#667eea'}
          >
            www.ariadelta.af
          </a>
        </div>
      </div>

      {/* CSS Animations + Responsive */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        /* Prevent layout shift on mobile address bar show/hide */
        .login-page { min-height: 100dvh; }

          background: linear-gradient(135deg, rgba(239, 68, 68, 0.2), rgba(220, 38, 38, 0.15)) !important;
          border-color: rgba(239, 68, 68, 0.4) !important;
          color: #fca5a5 !important;
        }
        
          background: linear-gradient(135deg, rgba(245, 158, 11, 0.2), rgba(217, 119, 6, 0.15)) !important;
          border-color: rgba(245, 158, 11, 0.4) !important;
          color: #fbbf24 !important;
        }

          background: rgba(0, 0, 0, 0.2) !important;
          color: #e5e7eb !important;
        }

          background: rgba(0, 0, 0, 0.2) !important;
          color: #e5e7eb !important;
        }

        /* Responsive tweaks */
        @media (max-width: 768px) {
          .login-page { padding: 12px; }
          .login-card { padding: 28px; border-radius: 18px; max-width: 420px; }
          .login-card h1 { font-size: 24px !important; }
          .login-card input { font-size: 16px !important; padding: 12px 12px 12px 42px !important; }
          .remember-me { margin-bottom: 16px !important; }
          .login-card form { margin-bottom: 16px !important; }
          .header-icon { width: 52px !important; height: 52px !important; }
          .login-card button[type='submit'] { padding: 12px !important; font-size: 16px !important; }
        }

        /* Desktop specific styling */
        @media (min-width: 1024px) {
          .login-card { 
            max-width: 520px !important; 
            padding: 32px !important;
          }
          .login-card h1 { font-size: 26px !important; }
          .login-card p { font-size: 15px !important; }
          .login-card input { 
            font-size: 15px !important; 
            padding: 14px 14px 14px 44px !important; 
          }
          .login-card button[type='submit'] { 
            padding: 14px !important; 
            font-size: 15px !important; 
          }
          .header-icon { 
            width: 60px !important; 
            height: 60px !important; 
          }
          .login-card .header { margin-bottom: 24px !important; }
          .login-card .header-icon { margin: 0 auto 16px !important; }
          .login-card form { margin-bottom: 20px !important; }
          .login-card .remember-me { margin-bottom: 20px !important; }
          .login-card .error-message { margin-bottom: 16px !important; padding: 10px !important; }
        }

        @media (max-width: 420px) {
          .login-card { padding: 20px; border-radius: 16px; max-width: 100%; }
          .login-card h1 { font-size: 22px !important; }
          .login-card p { font-size: 14px !important; }
          .login-card input { font-size: 15px !important; padding: 12px 10px 12px 40px !important; }
          .remember-me { margin-bottom: 12px !important; }
          .login-card form { margin-bottom: 16px !important; }
          .login-card button[type='submit'] { padding: 12px !important; font-size: 15px !important; }
        }
      `}</style>
    </div>
  );
};

export default Login; 